import { ChipPanel } from '@design-system/chip-panel';
import { GridContainer, GridItem } from '@design-system/grid';
import { SideFilters } from '@design-system/side-filters';
import { ScreenRecognitionContext } from '@phx-husky/screen-recognition';
import React, { forwardRef, useContext, useMemo } from 'react';

import { useDisclosure } from '~/hooks/useDisclosure';
import { useItemFilters } from '~/hooks/useFilter';
import { mapItems, wrapIfItsList } from '~/mappers/itemListing';
import {
  FilterMapperResult,
  ItemListingTypes,
} from '~/mappers/itemListing/models';
import { TypographyUI } from '~/mappers/typography/models';

import { ItemListingCounter } from '../ItemListingCounter/ItemListingCounter';
import { ItemListingCounterProps } from '../ItemListingCounter/models';
import ItemListingDefault from '../ItemListingDefault';
import ItemListingDefaultFilter from '../ItemListingDefault/ItemListingDefaultFilter';
import ItemListingDefaultSortBy from '../ItemListingDefault/ItemListingDefaultSortBy';
import { ItemListingDefaultProps } from '../ItemListingDefault/models';
import { ItemListingLoadMore } from '../ItemListingLoadMore/ItemListingLoadMore';
import { ItemListingLoadMoreProps } from '../ItemListingLoadMore/models';
import { FilterOption, ItemListingProps } from '../models';

const ItemListingResult = forwardRef<HTMLDivElement, ItemListingProps>(
  ({ type, ...props }, ref) => {
    switch (type) {
      case 'numbered':
        return (
          <ItemListingCounter
            ref={ref}
            {...(props as ItemListingCounterProps)}
          />
        );
      case 'loadMore':
        return (
          <ItemListingLoadMore
            ref={ref}
            {...(props as ItemListingLoadMoreProps)}
          />
        );
      case 'default':
      default:
        return (
          <ItemListingDefault
            ref={ref}
            {...(props as ItemListingDefaultProps)}
          />
        );
    }
  }
);

const getItemName = (item) => {
  if (item.fields && item.fields.productData) {
    return item.fields.productData.productTitle;
  }

  if (item.productData?.productTitle) {
    return item.productData.productTitle;
  }

  return item.heading?.headline;
};

const defaultTypographyUI: TypographyUI = {
  variant: 'body',
  align: 'center',
  tag: 'p',
  size: 'm',
  weight: 'regular',
};

const ItemListingGrid = forwardRef<HTMLDivElement, ItemListingProps>(
  (
    {
      pageBlock,
      fluidGrid,
      id,
      filterSettings = {} as FilterMapperResult,
      rawItems,
      currentPageUid,
      uid,
      whereToBuyData,
      type,
      loadMoreButton,
      initialNumberArticles,
    },
    ref
  ) => {
    const {
      filtersTitle,
      filterSelection: {
        variant = FilterOption.TopFilters,
        showChipPanel = false,
        dropdownCategories,
      } = {},
      filterDialog: { openModal, closeModal, clearFilters } = {},
      filters,
      clearSelectedFilters,
      itemsName,
      sortBy,
      noResults: {
        text = 'No results found',
        typographyUI = defaultTypographyUI,
      } = {},
    } = filterSettings;

    const { isOpen: isSortingOpen, toggle: toggleSortingOpen } =
      useDisclosure(false);
    const { isMobile } = useContext(ScreenRecognitionContext);

    const itemsWithProperties = useMemo(
      () =>
        rawItems.items.map((item) => ({
          ...item,
          name: getItemName(item),
          updatedAt: item.updatedAt || '',
        })),
      [rawItems.items]
    );

    const {
      data,
      filter,
      handleFilterChange,
      sort,
      handleSortChange,
      getActiveCountForGroup,
      handleGroupReset,
      handleClearAllFilters,
      handleActiveGroup,
      activeGroup,
    } = useItemFilters(itemsWithProperties, { filters });

    const itemsAmount = data.length;

    const items = wrapIfItsList({
      tag: fluidGrid.tag as string,
      items: mapItems(
        { ...rawItems, items: data } as ItemListingTypes,
        currentPageUid,
        whereToBuyData
      ),
    });

    if (
      variant === FilterOption.SideFilters &&
      (!openModal || !closeModal || !clearFilters)
    ) {
      console.warn(
        'Side filters were chosen, but props require openModal, closeModal and clearFilters to be passed as props.'
      );

      return null;
    }

    return filter && variant === FilterOption.SideFilters ? (
      <GridContainer className="cds-listing-filter" columnSpacing={4}>
        <GridItem md={3}>
          <SideFilters
            filters={filters}
            filter={filter}
            handleFilterChange={handleFilterChange}
            filtersTitle={filtersTitle}
            handleClearAllFilters={handleClearAllFilters}
            clearFilters={clearFilters!}
            closeModal={closeModal!}
            openModal={openModal!}
            itemsAmount={itemsAmount}
            dropdownCategories={dropdownCategories}
          />
        </GridItem>
        <GridItem md={9}>
          <ChipPanel
            showChipPanel={showChipPanel}
            filter={filter}
            handleFilterChange={handleFilterChange}
          />
          <ItemListingResult
            pageBlock={pageBlock}
            itemsName={itemsName}
            itemsAmount={itemsAmount}
            fluidGrid={fluidGrid}
            rawItems={rawItems}
            data={data}
            currentPageUid={currentPageUid}
            filters={filters}
            text={text}
            typographyUI={typographyUI}
            id={id}
            uid={uid}
            ref={ref}
            whereToBuyData={whereToBuyData}
            type={type}
            items={items}
            initialNumberArticles={
              initialNumberArticles === null ? 6 : initialNumberArticles
            }
            loadMoreButton={loadMoreButton}
          />
        </GridItem>
      </GridContainer>
    ) : (
      <>
        {filters && (
          <ItemListingDefaultFilter
            filters={filters}
            clearSelectedFilters={clearSelectedFilters}
            getActiveCountForGroup={getActiveCountForGroup}
            handleFilterChange={handleFilterChange}
            handleGroupReset={handleGroupReset}
            filter={filter}
            activeGroup={activeGroup}
            handleActiveGroup={handleActiveGroup}
            isMobile={isMobile}
          />
        )}
        {sortBy && (
          <ItemListingDefaultSortBy
            handleSortChange={handleSortChange}
            itemsAmount={itemsAmount}
            isSortingOpen={isSortingOpen}
            toggleSortingOpen={toggleSortingOpen}
            sort={sort}
            itemsName={itemsName}
            sortBy={sortBy}
          />
        )}
        <ItemListingResult
          pageBlock={pageBlock}
          itemsName={itemsName}
          itemsAmount={itemsAmount}
          fluidGrid={fluidGrid}
          rawItems={rawItems}
          data={data}
          currentPageUid={currentPageUid}
          filters={filters}
          text={text}
          typographyUI={typographyUI}
          id={id}
          uid={uid}
          ref={ref}
          whereToBuyData={whereToBuyData}
          type={type}
          items={items}
          initialNumberArticles={
            initialNumberArticles === null ? 6 : initialNumberArticles
          }
          loadMoreButton={loadMoreButton}
        />
      </>
    );
  }
);

export default ItemListingGrid;
